import React from 'react';

const windowGlobal = typeof window !== 'undefined' && window;

interface IStorageOptions {
	/** We always store in session; we can also store in local storage if told to remember forever */
	rememberForever: boolean;
	/** Set to true to use default value as initial value instead of any stored value */
	useDefault?: boolean;
	/** Set to true to use default and store it immediately to avoid render loops */
	useAndStoreDefault?: boolean;
	/** Callback after value is initialized */
	onInitialize?: (itemKey: string, itemValue: any) => void;
}

const defaultStorageOptions: IStorageOptions = {
	rememberForever: false,
	useDefault: false,
	useAndStoreDefault: false,
	onInitialize: () => {}
};

export function useStateWithStorage<T>(itemKey: string, itemDefault: T, itemOptions?: IStorageOptions): [T, React.Dispatch<React.SetStateAction<T>>] {
	const [initialized, setInitialized] = React.useState<boolean>(false);

	const options = React.useMemo<IStorageOptions>(() => {
		return {...defaultStorageOptions, ...itemOptions};
	}, [itemOptions]);

	// Set initial value in state
	const [value, setValue] = React.useState<T>(() => {
		// Use default value if requested
		if (options.useAndStoreDefault) {
			storeItem(itemKey, itemDefault, options.rememberForever);
			return itemDefault;
		}
		if (options.useDefault) return itemDefault;
		if (options.rememberForever) {
			// Always use session value if set, even if set to remember forever
			const sessionValue = getSessionItem(itemKey, undefined);
			if (sessionValue !== undefined) {
				return sessionValue;
			}
			// Otherwise use default value with the intent to update from local storage when ready
			else {
				return itemDefault;
			}
		}
		return getSessionItem(itemKey, itemDefault);
	});

	// On component mount, update from local storage if necessary
	React.useEffect(() => {
		const sessionValue = getSessionItem(itemKey, undefined);
		if (options.rememberForever && sessionValue === undefined)  {
			getLocalItemPromise(itemKey, itemDefault).then(storedValue => {
				setValue(storedValue as T);
				setInitialized(true);
			});
		}
		else {
			setInitialized(true);
		}
	}, []);

	// Send message that value is done initializing and what the value is
	React.useEffect(() => {
		if (initialized && options?.onInitialize) options.onInitialize(itemKey, value);
	}, [initialized]);

	// Update stored value when value changed in state
	React.useEffect(() => {
		// Remove from store (or ignore) if new value is undefined or default
		if (value === undefined || value === itemDefault) {
			removeStoredItem(itemKey);
		}
		else {
			storeItem(itemKey, value, options.rememberForever);
		}
	}, [value]);

	return [value, setValue];
}

// Use JSON.stringify and JSON.parse to preserve item types when storing, getting
const storeItem = (itemKey: string, itemValue: any, useLocalStorage: boolean) => {
	if (windowGlobal && windowGlobal.sessionStorage)
		windowGlobal.sessionStorage.setItem(itemKey, JSON.stringify(itemValue));
	if (windowGlobal && windowGlobal.localStorage && useLocalStorage) {
		windowGlobal.localStorage.setItem(itemKey, JSON.stringify(itemValue));
	}
	// Remove locally stored item if local storage no longer needed, but item currently saved there
	else if (windowGlobal && windowGlobal.localStorage) {
		windowGlobal.localStorage.removeItem(itemKey);
	}
};

const getSessionItem = (itemKey: string, itemDefault: any) => {
	if (windowGlobal && windowGlobal.sessionStorage) {
		let sessionValue = windowGlobal.sessionStorage.getItem(itemKey);
		if (sessionValue) {
			return JSON.parse(sessionValue);
		}
	}
	return itemDefault;
};

const getLocalItemPromise = (itemKey: string, itemDefault: any) => {
	return new Promise(resolve => {
		if (windowGlobal && windowGlobal.localStorage) {
			let localValue = windowGlobal.localStorage.getItem(itemKey);
			if (localValue) {
				resolve(JSON.parse(localValue as string));
				return;
			}
		}
		resolve(itemDefault);
	});
};

const removeStoredItem = (itemKey: string) => {
	if (windowGlobal && windowGlobal.sessionStorage)
		windowGlobal.sessionStorage.removeItem(itemKey);
	if (windowGlobal && windowGlobal.localStorage)
		windowGlobal.localStorage.removeItem(itemKey);
};
